// M004-F0779 / WebUI #251: a <video> that plays a signed stream URL renews the URL
// mid-play and resumes where the viewer was. The Choreo stream token is a 10-minute
// HMAC, re-checked on every Range request, so playback past its expiry (or a paused
// tab resumed after it) fails with a media error unless the URL is re-minted.
//
// Core part: createStreamRefresher is a plain state machine (no React). It owns the
// src, the renewal, the seek/resume and the failure cap; the <video> just forwards
// its events to it. useRefreshingStream is the React wrapper that exposes
// { src, status, error, retry, videoProps } for a <video> to spread.
//
// Failure model: every error that arrives before playing, and every failed mint,
// counts as one failure. Reaching `playing` clears the count. Three failures inside
// windowMs put the player in 'failed' with a Retry; retry() mints again from the
// remembered time.

import { useEffect, useRef, useState } from 'react';

export const STREAM_FAILED_MESSAGE = 'This video could not be played.';

export function createStreamRefresher({ mint, now = Date.now, safetyMs = 15000, maxFailures = 3, windowMs = 60000 }) {
  let state = { src: '', status: 'loading', error: '' };
  let expiresAt = NaN;
  let minting = false;      // single-flight: a mint is in flight, every event is ignored
  let awaitingLoad = false; // a fresh src was set and has not reached loadedmetadata yet
  let resume = null;        // { time, playing } captured at the first renewal, kept until loaded
  let failures = [];        // timestamps of failures inside the window
  let disposed = false;
  const listeners = new Set();

  const emit = (next) => {
    state = next;
    listeners.forEach((listener) => listener(state));
  };

  function recordFailure() {
    const at = now();
    failures = failures.filter((t) => at - t < windowMs).concat(at);
    return failures.length >= maxFailures;
  }

  function fail() {
    minting = false;
    awaitingLoad = false;
    emit({ src: '', status: 'failed', error: STREAM_FAILED_MESSAGE });
  }

  async function mintNow(status) {
    minting = true;
    emit({ src: state.src, status, error: '' });
    let result;
    try {
      result = await mint();
      if (!result || !result.url) throw new Error('No stream URL was returned.');
    } catch (error) {
      if (disposed) return;
      minting = false;
      if (recordFailure()) { fail(); return; }
      await mintNow(status);
      return;
    }
    if (disposed) return;
    minting = false;
    expiresAt = Number.isFinite(result.expiresAt) ? result.expiresAt : NaN;
    awaitingLoad = true;
    emit({ src: result.url, status: 'ready', error: '' });
  }

  function renew(video) {
    if (minting || disposed || state.status === 'failed') return;
    if (!resume && video) resume = { time: video.currentTime || 0, playing: !video.paused && !video.ended };
    if (recordFailure()) { fail(); return; }
    mintNow('renewing');
  }

  return {
    start() {
      if (disposed || minting || state.status !== 'loading' || state.src) return;
      mintNow('loading');
    },
    onError(video) {
      renew(video);
    },
    onStall(video) {
      if (minting || awaitingLoad || disposed || state.status === 'failed') return;
      // A token of unknown expiry is treated as due: a stall then re-mints rather than hangs.
      const due = !Number.isFinite(expiresAt) || now() >= expiresAt - safetyMs;
      if (due) renew(video);
    },
    onLoadedMetadata(video) {
      if (disposed || minting || !awaitingLoad) return;
      awaitingLoad = false;
      if (!resume) return;
      const { time, playing } = resume;
      resume = null;
      video.currentTime = time;
      if (playing) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }
    },
    onPlaying() {
      failures = [];
      if (minting) return;
      awaitingLoad = false;
      resume = null;
    },
    retry() {
      if (disposed || minting || state.status !== 'failed') return;
      failures = [];
      mintNow('renewing');
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    getState() {
      return state;
    },
    dispose() {
      disposed = true;
      listeners.clear();
    },
  };
}

const IDLE_STREAM = { src: '', status: 'loading', error: '' };

// `mint` returns {url, expiresAt} (expiresAt in epoch ms). `deps` restarts the stream
// when they change, like a useEffect dependency list. `active: false` mints nothing.
export function useRefreshingStream(mint, deps, { active = true } = {}) {
  const mintRef = useRef(mint);
  mintRef.current = mint;
  const refresherRef = useRef(null);
  const [state, setState] = useState(IDLE_STREAM);

  useEffect(() => {
    if (!active) {
      setState(IDLE_STREAM);
      return undefined;
    }
    const refresher = createStreamRefresher({ mint: () => mintRef.current() });
    refresherRef.current = refresher;
    const unsubscribe = refresher.subscribe(setState);
    refresher.start();
    return () => {
      unsubscribe();
      refresher.dispose();
      refresherRef.current = null;
    };
  }, [active, ...deps]);

  const videoProps = {
    onError: (event) => refresherRef.current?.onError(event?.currentTarget),
    onStalled: (event) => refresherRef.current?.onStall(event?.currentTarget),
    onWaiting: (event) => refresherRef.current?.onStall(event?.currentTarget),
    onLoadedMetadata: (event) => refresherRef.current?.onLoadedMetadata(event?.currentTarget),
    onPlaying: () => refresherRef.current?.onPlaying(),
  };

  return {
    src: state.src,
    status: state.status,
    error: state.error,
    retry: () => refresherRef.current?.retry(),
    videoProps,
  };
}
